import { BookingFormMode } from "PFApp/booking/components/booking_form";
import { useDetailsPanelsActions } from "PFApp/booking/components/details_panel";
import ViewInBookingModuleButton from "PFApp/components/view_in_booking_module_button/view_in_booking_module_button";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import useIsAvailabilityPermitted from "PFApp/use_is_availability_permitted";
import { ActionIcon } from "PFComponents/action_icon";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { MultiToggle, Option } from "PFComponents/multi_toggle";
import { useBookingApprovalsInvalidate } from "PFCore/hooks/queries/bookings/approvals/use_booking_approvals_invalidate";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { AccessLevel, Profile } from "PFTypes";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useProfileBookingsContext } from "../../../../common/profile_bookings_context";
import css from "./availability_section_toolbar.module.scss";
import { CopyCalendarLinkButton } from "./copy_calendar_link_button";

type HeaderToggleType = "calendar" | "list";

type AvailabilitySectionToolbarProps = {
  activeTab: HeaderToggleType;
  setActiveTab: (newTab: HeaderToggleType) => void;
  profile: Profile;
  isLoading: boolean;
  isMe: boolean;
  onExpand: () => void;
  isExpanded: boolean;
  shouldHideExpandAction: boolean;
};

export const AvailabilitySectionToolbar = ({
  activeTab,
  setActiveTab,
  profile,
  isMe,
  isExpanded,
  onExpand,
  shouldHideExpandAction,
  isLoading
}: AvailabilitySectionToolbarProps) => {
  const canEditAvailability = useIsAvailabilityPermitted(profile, [AccessLevel.ReadWrite]) && isMe;
  const { t } = useTranslation(["profiles", "availability"]);

  const { refreshBookings } = useProfileBookingsContext();
  const { invalidate: invalidateBookingApprovals } = useBookingApprovalsInvalidate();

  const { openBookingFormSidePanel } = useDetailsPanelsActions();

  const options = useMemo<Option<HeaderToggleType>[]>(
    () => [
      {
        id: "calendar",
        value: <Icon name="calendar" size="sm" />,
        title: t("profiles:show.parts.availability.viewBookingsCalendar")
      },
      {
        id: "list",
        value: <Icon name="list" size="sm" />,
        title: t("profiles:show.parts.availability.viewBookingsList")
      }
    ],
    [t]
  );

  const { canProfileCreateBooking, canCreateEngagementBookings } = useBookingModulePermissions();
  const canSeeBookingModule = canProfileCreateBooking(profile.id);
  const { checkWriteAccessToAtLeastOneCategory } = useBookingCategories();
  const hasWriteAccessToAtLeastOneCategoryNonDemand = checkWriteAccessToAtLeastOneCategory({
    demandRelated: false
  });
  const hasWriteAccessToAtLeastOneCategoryDemand = checkWriteAccessToAtLeastOneCategory({
    demandRelated: true
  });
  const isCreateEnabled =
    hasWriteAccessToAtLeastOneCategoryNonDemand ||
    (canCreateEngagementBookings && hasWriteAccessToAtLeastOneCategoryDemand);

  const handleBookingsUpdate = () => {
    invalidateBookingApprovals();
    refreshBookings();
  };

  return (
    <div className={css.container}>
      <div className={css.content}>
        <MultiToggle<HeaderToggleType>
          defaultSelected={activeTab}
          options={options}
          onChange={setActiveTab}
          classes={{ container: css.multiToggle }}
        />
        {!shouldHideExpandAction && (
          <ActionIcon
            aria-expanded={isExpanded}
            title={t(
              isExpanded
                ? "profiles:show.parts.availability.collapseAvailabilitySection"
                : "profiles:show.parts.availability.expandAvailabilitySection"
            )}
            name={isExpanded ? "collapse" : "expand"}
            size="sm"
            onClick={onExpand}
          />
        )}
        {isMe && <CopyCalendarLinkButton />}
        {canSeeBookingModule && <ViewInBookingModuleButton workforceMemberId={profile.id} />}
        {canEditAvailability && (
          <Button
            aria-label={t("availability:actions.book")}
            aria-haspopup="dialog"
            kind="primary"
            text={t("availability:actions.book")}
            onClick={() =>
              openBookingFormSidePanel(
                { profileId: profile.id },
                { mode: BookingFormMode.Create, onSuccess: handleBookingsUpdate }
              )
            }
            disabled={isLoading || !isCreateEnabled}
          />
        )}
      </div>
    </div>
  );
};
